import { type ApiErrorResponse, type TranslationIDs, useIntl } from '@capsicum/common';
import { type AxiosError } from 'axios';

import {
	useCreateUserAuthenticationMethod,
	useGetUserAuthenticationEventRequirements,
	useGetUserAuthenticationMethodsByMethod,
	useUpdateUserAuthenticationMethod,
	useValidateUserAuthenticationMethod
} from '../data-access';

import { type UpdateWithdrawalPasswordFormType, useWithdrawalPasswordForm } from './useWithdrawalPasswordForm';

type WithdrawalPasswordErrorsMap = Record<
	string,
	{ field: keyof UpdateWithdrawalPasswordFormType; message?: TranslationIDs }
>;

const WITHDRAWAL_PASSWORD_ERROR_CODES = {
	'393e12d0-8c13-4cfb-9f03-89fdf59abec9': {
		field: 'currentPassword',
		message: 'settings.withdrawalPassword.errors.tooManyAttempts'
	},
	'6e7b85c9-7a57-4911-abfd-9527e67eaa1d': {
		field: 'currentPassword',
		message: 'settings.withdrawalPassword.errors.tooManyAttemptsAccountLocked'
	},
	'f193a37b-d7a6-4142-88fe-7b3937d9ca17': {
		field: 'currentPassword',
		message: 'settings.withdrawalPassword.errors.invalidCurrentPassword'
	}
} as WithdrawalPasswordErrorsMap;

export const useWithdrawalPassword = () => {
	const { $t } = useIntl();
	const {
		data: userPasswordAuthenticationMethod,
		error: userPasswordAuthenticationMethodError,
		isLoading: isWithdrawalPasswordExistenceCheckLoading,
		refetch: refetchGetUserWithdrawalPassword
	} = useGetUserAuthenticationMethodsByMethod({ method: 'password' });
	const isWithdrawalPasswordDisabled =
		userPasswordAuthenticationMethod?.status && userPasswordAuthenticationMethod?.status === 'disabled';
	const withdrawalPasswordExists =
		!(userPasswordAuthenticationMethodError?.response?.status === 404) &&
		Boolean(userPasswordAuthenticationMethod) &&
		!isWithdrawalPasswordDisabled;

	const {
		data: createUserAuthenticationMethodResponse,
		isPending: isCreateUserAuthenticationMethodLoading,
		mutate: createUserAuthenticationMethod
	} = useCreateUserAuthenticationMethod();
	const { isPending: isValidateUserAuthenticationMethodLoading, mutate: validateUserAuthenticationMethod } =
		useValidateUserAuthenticationMethod();
	const { isPending: isUpdateUserAuthenticationMethodLoading, mutate: updateUserAuthenticationMethod } =
		useUpdateUserAuthenticationMethod();

	const { data: authenticationEventRequirements } = useGetUserAuthenticationEventRequirements('edit_auth_password', {
		enabled: withdrawalPasswordExists
	});
	const { newWithdrawalPasswordForm, updateWithdrawalPasswordForm } = useWithdrawalPasswordForm();

	const onSubmissionError = (error: AxiosError<ApiErrorResponse>) => {
		error.response?.data.errors?.forEach(({ code, title }) => {
			const errorDetails = WITHDRAWAL_PASSWORD_ERROR_CODES[code as string] || {
				field: 'currentPassword'
			};

			updateWithdrawalPasswordForm.setError(errorDetails.field, {
				message: errorDetails.message ? $t(errorDetails.message) : title
			});
		});
	};

	const submitNewWithdrawalPasswordForm = async (onSuccess: () => void) => {
		const submitForm = newWithdrawalPasswordForm.handleSubmit(async ({ confirmNewPassword, newPassword }) => {
			if (newPassword !== confirmNewPassword) {
				newWithdrawalPasswordForm.setError('confirmNewPassword', {
					message: $t('settings.withdrawalPassword.errors.passwordsDoesNotMatch')
				});

				return;
			}
			createUserAuthenticationMethod(
				{
					method: 'password',
					payload: {
						enableFor2FA: true,
						secret: newPassword
					}
				},
				{ onError: onSubmissionError, onSuccess }
			);
		});

		submitForm();
	};

	const submitUpdateWithdrawalPasswordForm = async (onSuccess: () => void) => {
		const submitForm = updateWithdrawalPasswordForm.handleSubmit(
			({ confirmNewPassword, currentPassword, newPassword }) => {
				if (authenticationEventRequirements?.methods?.includes('password')) {
					if (newPassword !== confirmNewPassword) {
						updateWithdrawalPasswordForm.setError('confirmNewPassword', {
							message: $t('settings.withdrawalPassword.errors.passwordsDoesNotMatch')
						});

						return;
					}
					const updatePassword = () => {
						updateUserAuthenticationMethod(
							{
								method: 'password',
								payload: {
									enableFor2FA: true,
									secret: updateWithdrawalPasswordForm.getValues('newPassword')
								}
							},
							{ onError: onSubmissionError, onSuccess }
						);
					};

					validateUserAuthenticationMethod(
						{
							event: 'edit_auth_password',
							method: 'password',
							secret: currentPassword
						},
						{
							onError: onSubmissionError,
							onSuccess: updatePassword
						}
					);
				}
			}
		);

		submitForm();
	};

	const submitWithdrawalPassword = (
		onSuccess: ({ description, title }: { description: string; title: string }) => void
	) => {
		if (withdrawalPasswordExists) {
			submitUpdateWithdrawalPasswordForm(() => {
				refetchGetUserWithdrawalPassword();
				onSuccess({
					description: $t('settings.withdrawalPassword.passwordChangedDescription'),
					title: $t('settings.withdrawalPassword.passwordChangedTitle')
				});
			});
		} else {
			submitNewWithdrawalPasswordForm(() => {
				refetchGetUserWithdrawalPassword();
				onSuccess({
					description: $t('settings.withdrawalPassword.passwordCreatedDescription'),
					title: $t('settings.withdrawalPassword.passwordCreatedTitle')
				});
			});
		}
	};

	const formProvider = withdrawalPasswordExists ? updateWithdrawalPasswordForm : newWithdrawalPasswordForm;
	const withdrawalPasswordPageTexts = withdrawalPasswordExists
		? {
				confirmPasswordLabel: $t('settings.withdrawalPassword.confirmNewPassword'),
				newPasswordLabel: $t('settings.withdrawalPassword.newPassword'),
				pageTitle: $t('settings.withdrawalPassword.changePassword')
			}
		: {
				confirmPasswordLabel: $t('settings.withdrawalPassword.confirmPassword'),
				newPasswordLabel: $t('settings.withdrawalPassword.password'),
				pageTitle: $t('settings.withdrawalPassword.addPassword')
			};

	return {
		createUserAuthenticationMethodResponse,
		formProvider,
		isCreateUserAuthenticationMethodLoading,
		isSubmitLoading:
			isCreateUserAuthenticationMethodLoading ||
			isValidateUserAuthenticationMethodLoading ||
			isUpdateUserAuthenticationMethodLoading,
		isWithdrawalPasswordExistenceCheckLoading,
		newWithdrawalPasswordForm,
		submitWithdrawalPassword,
		updateWithdrawalPasswordForm,
		withdrawalPasswordExists,
		withdrawalPasswordPageTexts
	};
};
